import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";

// ── Colour helpers ─────────────────────────────────────────────────────────
const VERDICT_COLOR = {
  shortlisted: "#22c55e",
  conditional:  "#fbbf24",
  needs_practice: "#a78bfa",
  rejected:     "#e63946",
  pending:      "#60a5fa",
  completed:    "#94a3b8",
};
const vc = (v) => VERDICT_COLOR[v] || "#94a3b8";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }) + " IST";
}
function fmtDur(secs) {
  if (!secs) return "—";
  const m = Math.round(secs / 60);
  return `${m} min`;
}
function posLabel(id) {
  return (id || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Recording Modal ────────────────────────────────────────────────────────
function RecordingModal({ sessionId, title, onClose }) {
  const [url,     setUrl]     = useState(null);
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminMockRecordingUrl(sessionId)
      .then(({ url: u }) => { setUrl(u); setLoading(false); })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Recording unavailable");
        setLoading(false);
      });
  }, [sessionId]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "1.5rem",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%", maxWidth: 960,
          background: "#0d1430", borderRadius: 16,
          border: "1px solid rgba(230,57,70,0.3)",
          overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div style={{
          padding: "1rem 1.5rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#f1f1f1" }}>📹 Session Recording</div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, marginTop: 2 }}>{title}</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                style={{
                  background: "rgba(230,57,70,0.12)", border: "1px solid rgba(230,57,70,0.35)",
                  color: "#e63946", borderRadius: 8, padding: "6px 14px",
                  fontSize: 12, fontWeight: 700, textDecoration: "none",
                }}
              >
                ↗ Open in new tab
              </a>
            )}
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.6)", borderRadius: 8, padding: "6px 12px",
                fontSize: 13, cursor: "pointer",
              }}
            >✕ Close</button>
          </div>
        </div>

        {/* Video area */}
        <div style={{ padding: "1rem 1.5rem 1.5rem" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "4rem", color: "rgba(255,255,255,0.4)" }}>
              Loading recording…
            </div>
          )}
          {!loading && error && (
            <div style={{
              textAlign: "center", padding: "3rem",
              background: "rgba(230,57,70,0.06)", borderRadius: 12,
              border: "1px solid rgba(230,57,70,0.2)",
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
              <div style={{ color: "#e63946", fontWeight: 700, marginBottom: 8 }}>Recording Not Available</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 16 }}>
                {error.includes("Playback error")
                  ? "Recording file is missing from storage — this session was recorded before the upload fix. Take a new session to get a fresh recording."
                  : error}
              </div>
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-block",
                    background: "rgba(230,57,70,0.15)", border: "1px solid rgba(230,57,70,0.4)",
                    color: "#e63946", borderRadius: 8, padding: "8px 18px",
                    fontSize: 13, fontWeight: 700, textDecoration: "none",
                  }}
                >
                  ↗ Try opening stream directly
                </a>
              )}
            </div>
          )}
          {!loading && url && (
            <video
              key={url}
              src={url}
              controls
              autoPlay
              preload="auto"
              style={{
                width: "100%", borderRadius: 10,
                background: "#000", outline: "none",
                maxHeight: "60vh",
              }}
              onError={() => setError("Playback error — try opening in a new tab above")}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Session Detail Drawer ──────────────────────────────────────────────────
function SessionDrawer({ session, onClose, onWatchRecording, onForceEnd }) {
  const color = vc(session.verdict || session.stage);
  const [forceLoading, setForceLoading] = useState(false);
  return (
    <div style={{
      background: "#0b1128", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16, padding: "1.5rem",
      maxHeight: "85vh", overflowY: "auto",
    }}>
      {/* Drawer header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#f1f1f1" }}>
            {session.user_name || session.user_email}
          </div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 3 }}>
            {posLabel(session.position_id)}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: "rgba(255,255,255,0.4)",
          cursor: "pointer", fontSize: 18, lineHeight: 1,
        }}>✕</button>
      </div>

      {/* Score + verdict */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: "1.25rem" }}>
        {[
          { label: "Score", value: session.total_score != null ? `${session.total_score}/${session.total_max || 20}` : "—", color: color },
          { label: "Verdict", value: (session.verdict || session.stage || "—").replace(/_/g," "), color },
          { label: "Duration", value: fmtDur(session.duration_seconds), color: "#94a3b8" },
        ].map(({ label, value, color: c }) => (
          <div key={label} style={{
            background: "rgba(255,255,255,0.04)", borderRadius: 10,
            padding: "0.75rem", textAlign: "center",
          }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: c }}>{value}</div>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Force Score button — only for stuck pending sessions */}
      {session.stage !== "completed" && (
        <button
          onClick={async () => {
            if (!window.confirm("Force-score this session? The AI will evaluate whatever transcript exists and mark it completed.")) return;
            setForceLoading(true);
            try { await onForceEnd(session.id); } finally { setForceLoading(false); onClose(); }
          }}
          disabled={forceLoading}
          style={{
            width: "100%", background: "rgba(251,191,36,0.12)",
            border: "1px solid rgba(251,191,36,0.4)", color: "#fbbf24", borderRadius: 10,
            padding: "10px 0", fontWeight: 700, fontSize: 13,
            cursor: forceLoading ? "wait" : "pointer", marginBottom: 10,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {forceLoading ? "Scoring…" : "⚡ Force Score & Close"}
        </button>
      )}

      {/* Recording button */}
      {session.recording_s3_key && (
        <button
          onClick={() => onWatchRecording(session.id, `${session.user_name || session.user_email} · ${posLabel(session.position_id)}`)}
          style={{
            width: "100%", background: "linear-gradient(135deg,#e63946,#c1121f)",
            border: "none", color: "#fff", borderRadius: 10,
            padding: "11px 0", fontWeight: 700, fontSize: 13,
            cursor: "pointer", marginBottom: "1.25rem",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          ▶ Watch Recording
        </button>
      )}

      {/* Meta */}
      <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 6, marginBottom: "1rem" }}>
        {[
          ["Email",    session.user_email],
          ["Started", fmt(session.started_at)],
          ["Ended",   fmt(session.ended_at)],
          ["Stage",   session.stage],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", gap: 8 }}>
            <span style={{ color: "rgba(255,255,255,0.35)", minWidth: 60 }}>{k}:</span>
            <span style={{ color: "rgba(255,255,255,0.8)" }}>{v || "—"}</span>
          </div>
        ))}
      </div>

      {/* Feedback */}
      {session.feedback_text && (
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>
            AI Feedback
          </div>
          <div style={{
            background: "rgba(255,255,255,0.03)", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.07)",
            padding: "0.875rem", color: "rgba(255,255,255,0.7)",
            fontSize: 12, lineHeight: 1.7,
          }}>
            {session.feedback_text}
          </div>
        </div>
      )}

      {/* KPI breakdown */}
      {Object.keys(session.kpi_scores || {}).length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>
            KPI Scores
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Object.entries(session.kpi_scores).map(([k, v]) => {
              const pct = v.max > 0 ? (v.score / v.max) * 100 : 0;
              const barColor = pct >= 70 ? "#22c55e" : pct >= 40 ? "#fbbf24" : "#e63946";
              return (
                <div key={k}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
                    <span style={{ color: "rgba(255,255,255,0.6)" }}>{k.replace(/_/g," ")}</span>
                    <span style={{ fontWeight: 700, color: barColor, fontFamily: "monospace" }}>{v.score}/{v.max}</span>
                  </div>
                  <div style={{ height: 5, background: "rgba(255,255,255,0.07)", borderRadius: 3 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 3, transition: "width 0.6s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Transcript preview */}
      {session.transcript?.length > 0 && (
        <div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: "uppercase" }}>
            Transcript · {session.transcript.length} messages
          </div>
          <div style={{
            background: "rgba(0,0,0,0.25)", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.06)",
            padding: "0.75rem", maxHeight: 260, overflowY: "auto",
          }}>
            {session.transcript.slice(-12).map((m, i) => (
              <div key={i} style={{ marginBottom: 8, fontSize: 12, lineHeight: 1.6 }}>
                <span style={{ color: m.role === "interviewer" ? "#e63946" : "#60a5fa", fontWeight: 700, marginRight: 6 }}>
                  {m.role === "interviewer" ? "AI:" : "You:"}
                </span>
                <span style={{ color: "rgba(255,255,255,0.65)" }}>
                  {m.content?.slice(0, 160)}{m.content?.length > 160 ? "…" : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ label, value, color, active, onClick, sublabel }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? `${color}15` : "rgba(255,255,255,0.04)",
        border: `1px solid ${active ? color + "55" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 12, padding: "1rem 0.75rem",
        textAlign: "center", cursor: "pointer",
        transition: "all 0.18s", outline: "none",
        transform: active ? "translateY(-2px)" : "none",
        boxShadow: active ? `0 6px 24px ${color}22` : "none",
      }}
    >
      <div style={{ fontFamily: "monospace", fontSize: 26, fontWeight: 900, color, lineHeight: 1 }}>{value ?? "—"}</div>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, marginTop: 5, letterSpacing: 0.3 }}>{label}</div>
      {sublabel && <div style={{ color: color, fontSize: 10, marginTop: 3, fontWeight: 600 }}>{sublabel}</div>}
    </button>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
const TABS = ["users", "sessions", "positions"];
const EMPTY_POSITION = {
  title: "", slug: "", short_description: "", experience: "",
  jd_text: "", knowledge_base: "", kpi_weights: "", total_weight: "20",
  selection_criteria: "", active: true,
};

export default function AdminMock() {
  const navigate = useNavigate();
  const [tab, setTab]         = useState("users");
  const [stats, setStats]     = useState(null);
  const [statFilter, setStatFilter] = useState(null); // "pending"|"shortlisted"|"rejected"|"completed"|null

  // Users
  const [users,    setUsers]    = useState([]);
  const [actingOn, setActingOn] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  // Sessions
  const [sessions,     setSessions]     = useState([]);
  const [viewSession,  setViewSession]  = useState(null);
  const [recModal,     setRecModal]     = useState(null); // { sessionId, title }

  // Positions
  const [positions,      setPositions]      = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm,     setCreateForm]     = useState(EMPTY_POSITION);
  const [creating,       setCreating]       = useState(false);

  useEffect(() => {
    refreshAll();
  }, []);

  async function refreshAll() {
    api.adminMockStats().then(setStats).catch(() => {});
    api.adminMockUsers().then((d) => setUsers(d.users || [])).catch(() => {});
    api.adminMockSessions().then((d) => setSessions(d.sessions || [])).catch(() => {});
    api.adminMockPositions().then((d) => setPositions(d.positions || [])).catch(() => {});
  }

  // ── per-user session map ───────────────────────────────────────────────
  const sessionsByUser = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      if (!map[s.user_email]) map[s.user_email] = [];
      map[s.user_email].push(s);
    });
    return map;
  }, [sessions]);

  // ── filtered sessions for sessions tab ────────────────────────────────
  const filteredSessions = useMemo(() => {
    if (!statFilter) return sessions;
    if (statFilter === "completed")  return sessions.filter((s) => s.stage === "completed");
    if (statFilter === "shortlisted") return sessions.filter((s) => s.verdict === "shortlisted");
    if (statFilter === "rejected")    return sessions.filter((s) => s.verdict === "rejected");
    if (statFilter === "pending")     return sessions.filter((s) => s.stage !== "completed");
    return sessions;
  }, [sessions, statFilter]);

  // ── approval handler ──────────────────────────────────────────────────
  async function handleApprove(email, approved, rejectionReason = "") {
    setActingOn(email);
    try {
      await api.adminMockApproveUser(email, approved, rejectionReason);
      toast.success(approved ? `${email} approved` : `${email} rejected`);
      setUsers((prev) => prev.map((u) =>
        u.email === email ? { ...u, approved, rejected: !approved } : u
      ));
      api.adminMockStats().then(setStats).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setActingOn(null);
    }
  }

  // ── session detail ─────────────────────────────────────────────────────
  async function openSession(id) {
    try {
      const s = await api.adminMockSessionDetail(id);
      setViewSession(s);
    } catch {
      toast.error("Could not load session details");
    }
  }

  // ── stat card click ────────────────────────────────────────────────────
  function handleStatClick(filter, targetTab) {
    setStatFilter(statFilter === filter ? null : filter);
    setTab(targetTab || "sessions");
    setViewSession(null);
  }

  // ── create position ────────────────────────────────────────────────────
  async function handleCreatePosition(e) {
    e.preventDefault();
    setCreating(true);
    try {
      let kpiWeights = {};
      try { kpiWeights = JSON.parse(createForm.kpi_weights); }
      catch {
        toast.error('KPI Weights must be valid JSON, e.g. {"intro":{"label":"Intro","max":5}}');
        setCreating(false);
        return;
      }
      await api.adminMockCreatePosition({
        ...createForm,
        total_weight: parseInt(createForm.total_weight, 10) || 20,
        kpi_weights: kpiWeights,
        coding_problems: [],
      });
      toast.success("Position created!");
      setShowCreateForm(false);
      setCreateForm(EMPTY_POSITION);
      api.adminMockPositions().then((d) => setPositions(d.positions || [])).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setCreating(false);
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#060a1f", color: "#f1f1f1", fontFamily: "'Inter','Calibri',sans-serif" }}>
      <Header />

      {/* Recording modal */}
      {recModal && (
        <RecordingModal
          sessionId={recModal.sessionId}
          title={recModal.title}
          onClose={() => setRecModal(null)}
        />
      )}

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "1.5rem" }}>

        {/* ── Breadcrumb ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.25rem" }}>
          <button onClick={() => navigate("/admin")} style={{
            background: "none", border: "none", color: "rgba(255,255,255,0.4)",
            fontSize: 13, cursor: "pointer", padding: 0,
          }}>← Admin</button>
          <span style={{ color: "rgba(255,255,255,0.2)" }}>/</span>
          <span style={{ color: "#e63946", fontSize: 13, fontWeight: 700 }}>Mock Interviews</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={refreshAll} style={{
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.5)", borderRadius: 8, padding: "5px 14px",
              fontSize: 12, cursor: "pointer",
            }}>⟳ Refresh</button>
            <button onClick={async () => {
              const btn = document.getElementById("fix-rec-btn");
              if (btn) { btn.disabled = true; btn.textContent = "🔧 Starting…"; }
              try {
                await api.adminMockFixRecordingDurations();
                if (btn) btn.textContent = "🔧 Running…";
                const poll = setInterval(async () => {
                  try {
                    const s = await api.adminMockFixRecordingDurationsStatus();
                    const repaired = s?.repaired ?? 0;
                    const skipped  = s?.skipped  ?? 0;
                    const failed   = s?.failed   ?? 0;
                    if (s?.done) {
                      clearInterval(poll);
                      if (btn) { btn.disabled = false; btn.textContent = "🔧 Fix Recording Durations"; }
                      toast.custom(() => (
                        <div style={{
                          background: "linear-gradient(135deg,#0d1117 0%,#111827 100%)",
                          border: "1px solid rgba(74,222,128,0.3)",
                          borderRadius: 14, padding: "16px 20px",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(74,222,128,0.08)",
                          minWidth: 280, fontFamily: "'Inter',sans-serif",
                        }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                            <span style={{ fontSize:18 }}>✅</span>
                            <span style={{ color:"#4ade80", fontWeight:700, fontSize:13, letterSpacing:"0.02em" }}>
                              Recording Durations Fixed
                            </span>
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                            {[
                              { label:"Repaired", val:repaired, color:"#4ade80" },
                              { label:"Skipped",  val:skipped,  color:"#facc15" },
                              { label:"Failed",   val:failed,   color:"#e63946" },
                            ].map(({ label, val, color }) => (
                              <div key={label} style={{ textAlign:"center", background:"rgba(255,255,255,0.04)", borderRadius:8, padding:"8px 4px", border:`1px solid ${color}20` }}>
                                <div style={{ color, fontWeight:800, fontSize:20, fontFamily:"monospace" }}>{val}</div>
                                <div style={{ color:"rgba(255,255,255,0.4)", fontSize:10, textTransform:"uppercase", letterSpacing:"0.08em", marginTop:2 }}>{label}</div>
                              </div>
                            ))}
                          </div>
                          {failed > 0 && (
                            <div style={{ marginTop:10, color:"rgba(239,68,68,0.7)", fontSize:11 }}>
                              {failed} file(s) not found in S3 — those sessions were recorded before the upload fix.
                            </div>
                          )}
                        </div>
                      ), { duration: 8000 });
                    } else if (!s?.running && !s?.done) {
                      clearInterval(poll);
                      if (btn) { btn.disabled = false; btn.textContent = "🔧 Fix Recording Durations"; }
                    }
                  } catch { clearInterval(poll); if (btn) { btn.disabled = false; btn.textContent = "🔧 Fix Recording Durations"; } }
                }, 3000);
              } catch (e) {
                if (btn) { btn.disabled = false; btn.textContent = "🔧 Fix Recording Durations"; }
                toast.custom(() => (
                  <div style={{
                    background:"linear-gradient(135deg,#0d1117,#1a0a0a)",
                    border:"1px solid rgba(230,57,70,0.4)", borderRadius:14, padding:"16px 20px",
                    boxShadow:"0 8px 32px rgba(0,0,0,0.6)", minWidth:260, fontFamily:"'Inter',sans-serif",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:18 }}>⚠️</span>
                      <div>
                        <div style={{ color:"#e63946", fontWeight:700, fontSize:13 }}>Fix Failed</div>
                        <div style={{ color:"rgba(255,255,255,0.4)", fontSize:11, marginTop:3 }}>
                          {e?.response?.data?.detail || e.message}
                        </div>
                      </div>
                    </div>
                  </div>
                ), { duration: 5000 });
              }
            }}
              id="fix-rec-btn"
              title="Patch WebM Duration metadata for all existing recordings so they show correct length"
              style={{
                background: "rgba(230,57,70,0.12)", border: "1px solid rgba(230,57,70,0.3)",
                color: "#e63946", borderRadius: 8, padding: "5px 14px",
                fontSize: 12, cursor: "pointer",
              }}>🔧 Fix Recording Durations</button>
          </div>
        </div>

        {/* ── Stats ── */}
        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, marginBottom: "1.5rem" }}>
            <StatCard
              label="Total Users"
              value={stats.total_users}
              color="#60a5fa"
              active={tab === "users" && !statFilter}
              onClick={() => { setTab("users"); setStatFilter(null); }}
              sublabel="click to view"
            />
            <StatCard
              label="Pending Approval"
              value={stats.pending_approvals}
              color="#fbbf24"
              active={statFilter === "pending" && tab === "users"}
              onClick={() => { setStatFilter(statFilter === "pending" ? null : "pending"); setTab("users"); }}
              sublabel={stats.pending_approvals > 0 ? "needs action" : null}
            />
            <StatCard
              label="Total Sessions"
              value={stats.total_sessions}
              color="#a78bfa"
              active={tab === "sessions" && !statFilter}
              onClick={() => { setTab("sessions"); setStatFilter(null); }}
              sublabel="click to view"
            />
            <StatCard
              label="Completed"
              value={stats.completed_sessions}
              color="#94a3b8"
              active={statFilter === "completed"}
              onClick={() => handleStatClick("completed")}
            />
            <StatCard
              label="Shortlisted"
              value={stats.shortlisted}
              color="#22c55e"
              active={statFilter === "shortlisted"}
              onClick={() => handleStatClick("shortlisted")}
            />
            <StatCard
              label="Not Selected"
              value={stats.rejected}
              color="#e63946"
              active={statFilter === "rejected"}
              onClick={() => handleStatClick("rejected")}
            />
          </div>
        )}

        {/* ── Tabs ── */}
        <div style={{ display: "flex", gap: 4, marginBottom: "1.5rem", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          {TABS.map((t) => (
            <button key={t} onClick={() => { setTab(t); setStatFilter(null); setViewSession(null); }} style={{
              background: tab === t ? "rgba(230,57,70,0.12)" : "none",
              border: "none",
              borderBottom: tab === t ? "2px solid #e63946" : "2px solid transparent",
              color: tab === t ? "#e63946" : "rgba(255,255,255,0.45)",
              padding: "9px 20px", fontWeight: 700, fontSize: 13,
              cursor: "pointer", borderRadius: "6px 6px 0 0",
              textTransform: "capitalize", letterSpacing: 0.5,
            }}>
              {t === "users" ? `Users${users.length ? ` (${users.length})` : ""}` : t === "sessions" ? `Sessions${sessions.length ? ` (${sessions.length})` : ""}` : "Positions"}
            </button>
          ))}
        </div>

        {/* ══════════════════ USERS TAB ══════════════════ */}
        {tab === "users" && (
          <div>
            <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                {statFilter === "pending" ? "⏳ Pending Approval" : "All Users"}
              </h3>
              {statFilter && (
                <button onClick={() => setStatFilter(null)} style={{
                  background: "none", border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.5)", borderRadius: 8, padding: "4px 12px",
                  fontSize: 11, cursor: "pointer",
                }}>✕ Clear filter</button>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {users
                .filter((u) => statFilter === "pending" ? (!u.approved && !u.rejected) : true)
                .map((u) => {
                  const userSessions = sessionsByUser[u.email] || [];
                  const isExpanded = expandedUser === u.email;
                  const latestSession = userSessions.sort((a, b) =>
                    new Date(b.started_at) - new Date(a.started_at))[0];

                  return (
                    <div key={u.email}>
                      {/* User row */}
                      <div style={{
                        background: isExpanded ? "rgba(230,57,70,0.06)" : "rgba(10,14,40,0.85)",
                        border: `1px solid ${isExpanded ? "rgba(230,57,70,0.25)" : "rgba(255,255,255,0.07)"}`,
                        borderRadius: isExpanded ? "12px 12px 0 0" : 12,
                        padding: "1rem 1.25rem",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        flexWrap: "wrap", gap: 10, cursor: "pointer",
                      }}
                        onClick={() => setExpandedUser(isExpanded ? null : u.email)}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          {/* Avatar initials */}
                          <div style={{
                            width: 38, height: 38, borderRadius: "50%",
                            background: "rgba(230,57,70,0.15)", border: "1px solid rgba(230,57,70,0.3)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 800, fontSize: 14, color: "#e63946", flexShrink: 0,
                          }}>
                            {(u.name || u.email).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{u.name || "—"}</div>
                            <div style={{ color: "#e63946", fontSize: 12, marginTop: 1 }}>{u.email}</div>
                            <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 11, marginTop: 1 }}>
                              Joined {fmt(u.created_at)}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                          {/* Session count badge */}
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontWeight: 900, fontSize: 20, color: "#a78bfa", fontFamily: "monospace" }}>
                              {userSessions.length}
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 10 }}>sessions</div>
                          </div>

                          {/* Latest verdict */}
                          {latestSession && (
                            <span style={{
                              background: `${vc(latestSession.verdict)}18`,
                              border: `1px solid ${vc(latestSession.verdict)}44`,
                              borderRadius: 20, padding: "3px 10px",
                              fontSize: 11, color: vc(latestSession.verdict), fontWeight: 700,
                            }}>
                              Latest: {latestSession.verdict || latestSession.stage}
                            </span>
                          )}

                          {/* Status */}
                          {u.approved ? (
                            <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 13 }}>✅ Approved</span>
                          ) : u.rejected ? (
                            <span style={{ color: "#e63946", fontWeight: 700, fontSize: 13 }}>❌ Rejected</span>
                          ) : (
                            <div style={{ display: "flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
                              <button disabled={actingOn === u.email} onClick={() => handleApprove(u.email, true)} style={{
                                background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)",
                                color: "#22c55e", borderRadius: 8, padding: "6px 14px",
                                fontWeight: 700, fontSize: 12, cursor: "pointer",
                              }}>{actingOn === u.email ? "…" : "✅ Approve"}</button>
                              <button disabled={actingOn === u.email} onClick={() => handleApprove(u.email, false)} style={{
                                background: "rgba(230,57,70,0.15)", border: "1px solid rgba(230,57,70,0.4)",
                                color: "#e63946", borderRadius: 8, padding: "6px 14px",
                                fontWeight: 700, fontSize: 12, cursor: "pointer",
                              }}>{actingOn === u.email ? "…" : "❌ Reject"}</button>
                            </div>
                          )}

                          <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 13 }}>
                            {isExpanded ? "▲" : "▼"}
                          </span>
                        </div>
                      </div>

                      {/* Expanded: user's sessions */}
                      {isExpanded && (
                        <div style={{
                          background: "rgba(6,10,31,0.9)", borderRadius: "0 0 12px 12px",
                          border: "1px solid rgba(230,57,70,0.15)", borderTop: "none",
                          padding: "0.75rem 1.25rem 1rem",
                        }}>
                          {userSessions.length === 0 ? (
                            <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, padding: "0.75rem 0" }}>
                              No sessions yet
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 600, letterSpacing: 1, marginBottom: 4, textTransform: "uppercase" }}>
                                All Attempts ({userSessions.length})
                              </div>
                              {userSessions
                                .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
                                .map((s, idx) => (
                                  <div key={s.id} style={{
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.07)",
                                    borderRadius: 10, padding: "0.75rem 1rem",
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    gap: 10, flexWrap: "wrap",
                                  }}>
                                    <div>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
                                        Attempt #{userSessions.length - idx} · {posLabel(s.position_id)}
                                      </div>
                                      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, marginTop: 2 }}>
                                        {fmt(s.started_at)} · {fmtDur(s.duration_seconds)}
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      {s.total_score != null && (
                                        <span style={{ fontFamily: "monospace", fontWeight: 800, color: vc(s.verdict), fontSize: 14 }}>
                                          {s.total_score}/{s.total_max || 20}
                                        </span>
                                      )}
                                      <span style={{
                                        background: `${vc(s.verdict)}18`, border: `1px solid ${vc(s.verdict)}44`,
                                        borderRadius: 20, padding: "2px 8px", fontSize: 10, color: vc(s.verdict), fontWeight: 700,
                                      }}>
                                        {s.verdict || s.stage}
                                      </span>
                                      {s.recording_s3_key && (
                                        <button
                                          onClick={() => setRecModal({ sessionId: s.id, title: `${u.name || u.email} · Attempt #${userSessions.length - idx}` })}
                                          style={{
                                            background: "rgba(230,57,70,0.12)", border: "1px solid rgba(230,57,70,0.3)",
                                            color: "#e63946", borderRadius: 8, padding: "4px 10px",
                                            fontSize: 11, fontWeight: 700, cursor: "pointer",
                                          }}
                                        >▶ Recording</button>
                                      )}
                                      <button
                                        onClick={() => { openSession(s.id); setTab("sessions"); }}
                                        style={{
                                          background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                                          color: "rgba(255,255,255,0.6)", borderRadius: 8, padding: "4px 10px",
                                          fontSize: 11, cursor: "pointer",
                                        }}
                                      >Details</button>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* ══════════════════ SESSIONS TAB ══════════════════ */}
        {tab === "sessions" && (
          <div style={{ display: "grid", gridTemplateColumns: viewSession ? "1fr 440px" : "1fr", gap: "1.5rem" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  {statFilter ? `Filtered: ${statFilter}` : "All Sessions"}
                  <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                    ({filteredSessions.length})
                  </span>
                </h3>
                {statFilter && (
                  <button onClick={() => setStatFilter(null)} style={{
                    background: "none", border: "1px solid rgba(255,255,255,0.15)",
                    color: "rgba(255,255,255,0.5)", borderRadius: 8, padding: "4px 12px",
                    fontSize: 11, cursor: "pointer",
                  }}>✕ Clear filter</button>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {filteredSessions
                  .slice()
                  .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
                  .map((s) => {
                    const color = vc(s.verdict || s.stage);
                    const active = viewSession?.id === s.id;
                    return (
                      <div key={s.id} onClick={() => openSession(s.id)} style={{
                        background: active ? "rgba(230,57,70,0.08)" : "rgba(10,14,40,0.85)",
                        border: `1px solid ${active ? "rgba(230,57,70,0.3)" : "rgba(255,255,255,0.07)"}`,
                        borderRadius: 10, padding: "0.875rem 1.1rem",
                        cursor: "pointer", transition: "all 0.15s",
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between", gap: 8, flexWrap: "wrap",
                      }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{s.user_name || s.user_email}</div>
                          <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginTop: 2 }}>
                            {posLabel(s.position_id)} · {fmt(s.started_at)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          {s.recording_s3_key && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setRecModal({ sessionId: s.id, title: `${s.user_name || s.user_email} · ${posLabel(s.position_id)}` }); }}
                              style={{
                                background: "rgba(230,57,70,0.1)", border: "1px solid rgba(230,57,70,0.3)",
                                color: "#e63946", borderRadius: 8, padding: "3px 8px",
                                fontSize: 10, fontWeight: 700, cursor: "pointer",
                              }}
                            >▶ REC</button>
                          )}
                          {s.total_score != null && (
                            <span style={{ fontFamily: "monospace", fontWeight: 800, color, fontSize: 14 }}>
                              {s.total_score}/{s.total_max || 20}
                            </span>
                          )}
                          <span style={{
                            background: `${color}18`, border: `1px solid ${color}44`,
                            borderRadius: 20, padding: "3px 10px", fontSize: 11, color, fontWeight: 700,
                          }}>
                            {s.verdict || s.stage}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                {filteredSessions.length === 0 && (
                  <div style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: "3rem" }}>
                    No sessions match this filter
                  </div>
                )}
              </div>
            </div>

            {/* Session detail drawer */}
            {viewSession && (
              <SessionDrawer
                session={viewSession}
                onClose={() => setViewSession(null)}
                onWatchRecording={(id, title) => setRecModal({ sessionId: id, title })}
                onForceEnd={async (id) => {
                  await api.adminMockForceEndSession(id);
                  // Refresh sessions list
                  const data = await api.adminMockSessions();
                  setSessions(data.sessions || []);
                }}
              />
            )}
          </div>
        )}

        {/* ══════════════════ POSITIONS TAB ══════════════════ */}
        {tab === "positions" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Open Positions</h3>
              <button onClick={() => setShowCreateForm((v) => !v)} style={{
                background: showCreateForm ? "rgba(255,255,255,0.06)" : "linear-gradient(135deg,#e63946,#c1121f)",
                border: "none", color: "#fff", borderRadius: 8,
                padding: "7px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                {showCreateForm ? "✕ Cancel" : "+ Create Position"}
              </button>
            </div>

            {showCreateForm && (
              <form onSubmit={handleCreatePosition} style={{
                background: "rgba(10,14,40,0.9)", border: "1px solid rgba(230,57,70,0.25)",
                borderRadius: 14, padding: "1.5rem", marginBottom: "1.5rem",
                display: "flex", flexDirection: "column", gap: 14,
              }}>
                <h4 style={{ color: "#e63946", fontWeight: 700, fontSize: 14, margin: 0 }}>New Position</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { key: "title",        label: "Job Title",        placeholder: "e.g. SAP CAP Java Developer" },
                    { key: "slug",         label: "Slug (unique ID)", placeholder: "e.g. sap-cap-java-v2" },
                    { key: "experience",   label: "Experience",       placeholder: "e.g. 3–6 years" },
                    { key: "total_weight", label: "Total KPI Weight", placeholder: "20" },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 600, letterSpacing: 0.5, display: "block", marginBottom: 5 }}>
                        {label.toUpperCase()}
                      </label>
                      <input required value={createForm[key]}
                        onChange={(e) => setCreateForm((f) => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder}
                        style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 12px", color: "#f1f1f1", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                      />
                    </div>
                  ))}
                </div>
                {[
                  { key: "short_description", label: "Short Description",    placeholder: "One-line summary", rows: 2 },
                  { key: "jd_text",           label: "Job Description",      placeholder: "Full JD text…", rows: 6 },
                  { key: "knowledge_base",    label: "Knowledge Base",       placeholder: "Key concepts for the AI interviewer…", rows: 6 },
                  { key: "selection_criteria",label: "Selection Criteria",   placeholder: "e.g. Score ≥ 14/20 → Shortlisted", rows: 2 },
                  { key: "kpi_weights",       label: "KPI Weights (JSON)",   placeholder: '{"intro":{"label":"Introduction","max":5}}', rows: 4 },
                ].map(({ key, label, placeholder, rows }) => (
                  <div key={key}>
                    <label style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 600, letterSpacing: 0.5, display: "block", marginBottom: 5 }}>
                      {label.toUpperCase()}
                    </label>
                    <textarea required rows={rows} value={createForm[key]}
                      onChange={(e) => setCreateForm((f) => ({ ...f, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 12px", color: "#f1f1f1", fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "monospace" }}
                    />
                  </div>
                ))}
                <button type="submit" disabled={creating} style={{
                  background: "linear-gradient(135deg,#e63946,#c1121f)", border: "none", color: "#fff",
                  borderRadius: 10, padding: "11px 0", fontWeight: 700, fontSize: 14,
                  cursor: creating ? "not-allowed" : "pointer", opacity: creating ? 0.7 : 1,
                }}>
                  {creating ? "Creating…" : "✓ Create Position"}
                </button>
              </form>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {positions.length === 0 ? (
                <div style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: "2rem" }}>
                  No admin-created positions yet. Built-in positions are always active.
                </div>
              ) : positions.map((p) => (
                <div key={p.id} style={{
                  background: "rgba(10,14,40,0.85)", border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 10, padding: "1rem 1.25rem",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.title}</div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 2 }}>
                      {p.experience || "—"} · {p.source === "builtin" ? "Built-in" : "Admin created"}
                    </div>
                  </div>
                  <span style={{
                    background: p.active ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.06)",
                    border: `1px solid ${p.active ? "rgba(34,197,94,0.35)" : "rgba(255,255,255,0.1)"}`,
                    color: p.active ? "#22c55e" : "rgba(255,255,255,0.4)",
                    borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700,
                  }}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "1rem", color: "rgba(255,255,255,0.2)", fontSize: 11, lineHeight: 1.7 }}>
              Built-in positions (SAP CAP Java Developer, SAP DevOps Expert) are always available.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
